"use client";

import { useMemo } from "react";
import type { Option } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  FOREWORD_FIELDS as F,
  type ForewordLabels,
  type ForewordPart,
  forewordIsReady,
  formatForeword,
} from "@/lib/tool-kit/citation-sources-books/foreword-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const FOREWORD_CITATION_GENERATOR_STRINGS = {
  formHeading: "Foreword and book details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the book's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  partLabel: "Which part are you citing?",
  partForeword: "Foreword",
  partIntroduction: "Introduction",
  partPreface: "Preface",
  partAfterword: "Afterword",
  writersLabel: "Who wrote the foreword, introduction or other part?",
  writersPlaceholder: "Bernard Knox",
  writersHint: "One per line, as First Last or Last, First.",
  authorsLabel: "Author(s) of the book",
  authorsPlaceholder: "Sophocles",
  authorsHint:
    "If the same person wrote both, enter the name in both boxes — the tool then drops the by.",
  titleLabel: "Title of the book",
  titlePlaceholder: "The three Theban plays",
  titleHint:
    "Sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  editionLabel: "Edition (optional)",
  editionPlaceholder: "2nd",
  translatorsLabel: "Translator(s) (optional)",
  translatorsPlaceholder: "Robert Fagles",
  editorsLabel: "Editor(s) of the book (optional)",
  editorsPlaceholder: "",
  publisherLabel: "Publisher",
  publisherPlaceholder: "Penguin Books",
  yearLabel: "Year of the edition you hold",
  yearPlaceholder: "2000",
  originalYearLabel: "Year first published (APA, optional)",
  originalYearPlaceholder: "1984",
  originalYearHint:
    "Adds (Original work published 1984) and the 1984/2000 form to the APA citation.",
  creditedLabel: "The writer is credited on the cover with “with” (APA)",
  partPagesLabel: "Pages of the part (MLA)",
  partPagesPlaceholder: "33-54",
  partPagesHint:
    "The first and last page of the foreword or introduction, as printed: 33-54 or xi-xxxvii.",
  pageLabel: "Page you quote (optional)",
  pagePlaceholder: "35",
  pageHint: "Goes into the in-text citations and the Chicago notes.",
  descriptionLabel: "Describe the part (MLA, optional)",
  descriptionPlaceholder: "Introduction to Antigone",
  descriptionHint:
    "Use this when a book has several introductions, so each entry says which one you mean.",
  shortTitleLabel: "Short title for notes (Chicago, optional)",
  shortTitlePlaceholder: "Three Theban Plays",
  shortTitleHint: "Used in the shortened note when no writer is given.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list (the book)",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited (the foreword or other part)",
  worksCitedBook: "Works Cited (the book itself, if you quote the book)",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 does not give a foreword its own reference entry: you cite the book, name the writer in your sentence, and add foreword by and the writer's surname to the in-text citation when you quote the foreword itself.",
  mlaNote:
    "MLA 9 starts the entry with the writer of the part, labels the part, then names the book and its author after by. The page numbers are those of the part. The last entry is the book on its own, with the writer named under other contributors — MLA lets you leave that name out.",
  chicagoNote:
    "Chicago 18 starts with the writer of the part and names the part in plain type: foreword to, introduction to. The writer of the book follows by. Chicago 18 no longer asks for the part's page range in a bibliography.",
  example: {
    part: "introduction",
    writers: "Bernard Knox",
    authors: "Sophocles",
    title: "The three Theban plays",
    translators: "Robert Fagles",
    publisher: "Penguin Books",
    year: "2000",
    partPages: "33-54",
    page: "35",
  },
} as const;

type Strings = typeof FOREWORD_CITATION_GENERATOR_STRINGS;

function textField(
  id: string,
  label: string,
  placeholder: string,
  hint?: string
): SourceField {
  return { id, kind: "text", label, placeholder, hint };
}

function partOptions(strings: Strings): readonly Option<ForewordPart>[] {
  return [
    { value: "foreword", label: strings.partForeword },
    { value: "introduction", label: strings.partIntroduction },
    { value: "preface", label: strings.partPreface },
    { value: "afterword", label: strings.partAfterword },
  ];
}

function peopleField(
  id: string,
  label: string,
  placeholder: string,
  hint?: string
): SourceField {
  return { id, kind: "people", label, placeholder, hint };
}

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.part,
      kind: "select",
      label: strings.partLabel,
      options: partOptions(strings),
    },
    peopleField(
      F.writers,
      strings.writersLabel,
      strings.writersPlaceholder,
      strings.writersHint
    ),
    peopleField(
      F.authors,
      strings.authorsLabel,
      strings.authorsPlaceholder,
      strings.authorsHint
    ),
    textField(
      F.title,
      strings.titleLabel,
      strings.titlePlaceholder,
      strings.titleHint
    ),
    textField(F.edition, strings.editionLabel, strings.editionPlaceholder),
    peopleField(
      F.translators,
      strings.translatorsLabel,
      strings.translatorsPlaceholder
    ),
    peopleField(F.editors, strings.editorsLabel, strings.editorsPlaceholder),
    textField(
      F.publisher,
      strings.publisherLabel,
      strings.publisherPlaceholder
    ),
    {
      id: F.year,
      kind: "date",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    {
      id: F.originalYear,
      kind: "date",
      label: strings.originalYearLabel,
      placeholder: strings.originalYearPlaceholder,
      hint: strings.originalYearHint,
    },
    { id: F.credited, kind: "checkbox", label: strings.creditedLabel },
    textField(
      F.partPages,
      strings.partPagesLabel,
      strings.partPagesPlaceholder,
      strings.partPagesHint
    ),
    textField(
      F.page,
      strings.pageLabel,
      strings.pagePlaceholder,
      strings.pageHint
    ),
    textField(
      F.description,
      strings.descriptionLabel,
      strings.descriptionPlaceholder,
      strings.descriptionHint
    ),
    textField(
      F.shortTitle,
      strings.shortTitleLabel,
      strings.shortTitlePlaceholder,
      strings.shortTitleHint
    ),
  ];
}

/** The widget's strings, as the formatter's labels. */
export function forewordLabels(strings: Strings): ForewordLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    worksCitedBook: strings.worksCitedBook,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function ForewordCitationGeneratorWidget() {
  const strings = useToolUi(FOREWORD_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => forewordLabels(strings), [strings]);
  const format = useMemo(
    () => (values: Record<string, string>) => formatForeword(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={forewordIsReady}
      strings={strings}
    />
  );
}
